import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  ThemeProvider as MuiThemeProvider,
  CssBaseline
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  DriveEta,
  DirectionsCar,
  Payment,
  Assessment,
  Support,
  Settings,
  Logout,
  VerifiedUser,
  Gavel,
  Notifications as NotificationBell,
  NotificationsActive,
  Article,
  Security,
  CardGiftcard,
  SmartToy,
  Bookmark,
  Forum
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import adminTheme from '../../utils/adminTheme';

const drawerWidth = 260;

interface AdminInfo {
  id: string;
  username: string;
  firstName: string;
  preferredName?: string;
  lastName: string;
  email: string;
  role: string;
  profilePicture?: string;
}

const adminPathPrefix = '/KP2lPQ25klr';

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: `${adminPathPrefix}/dashboard` },
  { text: 'Users', icon: <People />, path: `${adminPathPrefix}/users` },
  { text: 'Drivers', icon: <DriveEta />, path: `${adminPathPrefix}/drivers` },
  { text: 'Verification', icon: <VerifiedUser />, path: `${adminPathPrefix}/verification` },
  { text: 'Rides', icon: <DirectionsCar />, path: `${adminPathPrefix}/rides` },
  { text: 'Payments', icon: <Payment />, path: `${adminPathPrefix}/payments` },
  { text: 'Credits', icon: <CardGiftcard />, path: `${adminPathPrefix}/credits` },
  { text: 'Disputes', icon: <Gavel />, path: `${adminPathPrefix}/disputes` },
  { text: 'Reports', icon: <Assessment />, path: `${adminPathPrefix}/reports` },
  { text: 'Content', icon: <Article />, path: `${adminPathPrefix}/content` },
  { text: 'Notifications', icon: <NotificationsActive />, path: `${adminPathPrefix}/notifications` },
  { text: 'Support', icon: <Support />, path: `${adminPathPrefix}/support` },
  { text: 'Live Support Chat', icon: <Forum />, path: `${adminPathPrefix}/support-chat` },
  { text: 'Audit Logs', icon: <Security />, path: `${adminPathPrefix}/audit-logs` },
  { text: 'AI Usage', icon: <SmartToy />, path: `${adminPathPrefix}/ai-usage` },
  { text: 'Saved Rides', icon: <Bookmark />, path: `${adminPathPrefix}/saved-rides` },
  { text: 'Settings', icon: <Settings />, path: `${adminPathPrefix}/settings` }
];

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [adminInfo, setAdminInfo] = useState<AdminInfo | null>(null);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    // Load admin info from localStorage
    const storedAdmin = localStorage.getItem('adminInfo');
    if (storedAdmin) {
      setAdminInfo(JSON.parse(storedAdmin));
    }

    // Check auth
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate(`${adminPathPrefix}/login`);
    }

    // Verify token with backend; if invalid/expired, force logout
    const verifyToken = async () => {
      try {
        const apiUrl = process.env.NODE_ENV === 'production'
          ? 'https://rideclubnet.com/api'
          : 'http://localhost:3001/api';
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        await axios.get(`${apiUrl}/admin/auth/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err: any) {
        // Clear and redirect if unauthorized/forbidden
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminInfo');
          navigate(`${adminPathPrefix}/login`);
        }
      }
    };
    verifyToken();

    // Fetch pending tasks count
    fetchPendingTasks();
  }, [navigate]);

  const fetchPendingTasks = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? 'https://rideclubnet.com/api'
        : 'http://localhost:3001/api';
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${apiUrl}/admin/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const tasks = response.data.metrics.adminTasks;
      setPendingCount(
        tasks.pendingVerifications +
        tasks.openDisputes +
        tasks.openSupportTickets +
        (tasks.openMessageReports || 0)
      );
    } catch (error) {
      console.error('Failed to fetch pending tasks:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      const apiUrl = process.env.NODE_ENV === 'production'
        ? 'https://rideclubnet.com/api'
        : 'http://localhost:3001/api';
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${apiUrl}/admin/auth/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminInfo');
      navigate(`${adminPathPrefix}/login`);
    }
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6" noWrap component="div" fontWeight="bold">
          RideClub Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={isActive}
                onClick={() => navigate(item.path)}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText',
                    '& .MuiListItemIcon-root': {
                      color: 'primary.contrastText'
                    }
                  }
                }}
              >
                <ListItemIcon sx={{ color: isActive ? 'inherit' : 'action.active' }}>
                  {item.text === 'Verification' && pendingCount > 0 ? (
                    <Badge badgeContent={pendingCount} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <MuiThemeProvider theme={adminTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex' }}>
        <AppBar
          position="fixed"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` }
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {menuItems.find(item => item.path === location.pathname)?.text || 'Admin Panel'}
            </Typography>
            <IconButton color="inherit" sx={{ mr: 2 }} onClick={() => navigate(`${adminPathPrefix}/support`)}>
              <Badge badgeContent={pendingCount} color="error">
                <NotificationBell />
              </Badge>
            </IconButton>
            <IconButton onClick={handleMenuClick} sx={{ p: 0 }}>
              <Avatar
                alt={adminInfo?.preferredName || adminInfo?.firstName}
                src={adminInfo?.profilePicture}
                sx={{ bgcolor: 'secondary.main' }}
              >
                {adminInfo?.firstName?.[0]}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem disabled>
                <Typography variant="body2">
                  {adminInfo?.preferredName || adminInfo?.firstName} {adminInfo?.lastName ? adminInfo?.lastName.charAt(0) + '.' : ''}
                </Typography>
              </MenuItem>
              <MenuItem disabled>
                <Typography variant="caption" color="text.secondary">
                  {adminInfo?.role?.replace(/_/g, ' ')}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        >
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', sm: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: 'none', sm: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth }
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3, md: 4 },
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            minHeight: '100vh',
            bgcolor: '#F8FAFC',
          }}
        >
          <Toolbar />
          <Outlet />
        </Box>
      </Box>
    </MuiThemeProvider>
  );
};

export default AdminLayout;
